/**
 * ScenePicker — Modal body for selecting an existing scene
 * and choosing a link mode (reference / copy own / move own).
 *
 * Two-step flow rendered as `body` inside addModal():
 *   Step 1: Search & select a scene
 *   Step 2: Choose link mode (with move-own availability check)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Button, Modal, Form, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { CameraReels, Search, InfoCircle } from 'react-bootstrap-icons';
import { apiClient } from '../api/client';
import type { SceneApiResponse } from '../../../Shared/src/@types';

type LinkMode = 'reference' | 'copy-own' | 'move-own';

interface ScenePickerProps {
  /** Scene IDs already in this story (to grey them out) */
  existingSceneIds: number[];
  onSelect: (sceneId: number, mode: LinkMode) => void;
  onCancel: () => void;
}

const ScenePicker: React.FC<ScenePickerProps> = ({ existingSceneIds, onSelect, onCancel }) => {
  // Step state
  const [step, setStep] = useState<'pick' | 'mode'>('pick');

  // Step 1: Scene list
  const [scenes, setScenes] = useState<SceneApiResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedSceneId, setSelectedSceneId] = useState<number | null>(null);

  // Step 2: Link mode
  const [mode, setMode] = useState<LinkMode>('reference');
  const [moveDisabled, setMoveDisabled] = useState(true);
  const [moveReason, setMoveReason] = useState('');
  const [checkingLinks, setCheckingLinks] = useState(false);

  // Fetch scenes on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.getScenes();
        if (res.success) setScenes(res.data);
      } catch (e) {
        console.error('Failed to fetch scenes:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Filter by search
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return scenes;
    return scenes.filter((s) => s.name.toLowerCase().includes(q));
  }, [scenes, search]);

  // When user picks a scene → transition to mode step
  const handlePickScene = async (sceneId: number) => {
    setSelectedSceneId(sceneId);
    setMode('reference');
    setStep('mode');

    // Check whether move-own is possible
    setCheckingLinks(true);
    setMoveDisabled(true);
    setMoveReason('');
    try {
      const res = await apiClient.getSceneStoryLinks(sceneId);
      if (res.success) {
        if (res.data.count > 0) {
          const names = res.data.links.map((l) => l.storyName).join(', ');
          setMoveDisabled(true);
          setMoveReason(`This scene is already linked to: ${names}`);
        } else {
          setMoveDisabled(false);
        }
      }
    } catch {
      setMoveDisabled(true);
      setMoveReason('Could not check scene links');
    } finally {
      setCheckingLinks(false);
    }
  };

  const selectedScene = scenes.find((s) => s.id === selectedSceneId);

  // =========================================================================
  // STEP 1: Pick a scene
  // =========================================================================
  if (step === 'pick') {
    return (
      <>
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{ position: 'relative' }}>
            <Search
              size={14}
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6b6966',
                pointerEvents: 'none',
              }}
            />
            <Form.Control
              type="text"
              placeholder="Search scenes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              style={{ paddingLeft: 32 }}
            />
          </div>
        </div>

        <div
          style={{
            maxHeight: 360,
            overflowY: 'auto',
            padding: '0 16px',
          }}
        >
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-muted text-center py-3">No scenes found</p>
          ) : (
            filtered.map((scene) => {
              const alreadyLinked = existingSceneIds.includes(scene.id);
              return (
                <div
                  key={scene.id}
                  onClick={() => !alreadyLinked && handlePickScene(scene.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    marginBottom: 4,
                    borderRadius: 8,
                    cursor: alreadyLinked ? 'default' : 'pointer',
                    opacity: alreadyLinked ? 0.4 : 1,
                    background: 'rgba(26,26,36,0.5)',
                    border: '1px solid transparent',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!alreadyLinked) {
                      (e.currentTarget as HTMLDivElement).style.borderColor = '#b87333';
                      (e.currentTarget as HTMLDivElement).style.background = 'rgba(26,26,36,0.8)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'transparent';
                    (e.currentTarget as HTMLDivElement).style.background = 'rgba(26,26,36,0.5)';
                  }}
                >
                  <CameraReels size={24} style={{ color: '#4a9eff', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {scene.name}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b6966' }}>
                      ID {scene.id}
                      {alreadyLinked && ' — already in this story'}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <Modal.Footer>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </Modal.Footer>
      </>
    );
  }

  // =========================================================================
  // STEP 2: Choose link mode
  // =========================================================================
  return (
    <>
      <div style={{ padding: '0 16px 16px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'rgba(26,26,36,0.5)',
            marginBottom: 16,
          }}
        >
          <CameraReels size={24} style={{ color: '#4a9eff', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{selectedScene?.name}</div>
            <div style={{ fontSize: 11, color: '#6b6966' }}>ID {selectedSceneId}</div>
          </div>
        </div>

        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: '#e8e6e3' }}>
          How should this scene be linked?
        </div>

        <Form>
          {/* Reference */}
          <Form.Check
            type="radio"
            id="link-reference"
            name="linkMode"
            label={
              <div>
                <strong>Reference</strong>
                <div style={{ fontSize: 12, color: '#a8a5a0' }}>
                  Link to the original scene. Edits affect all stories using it.
                </div>
              </div>
            }
            checked={mode === 'reference'}
            onChange={() => setMode('reference')}
            className="mb-3"
          />

          {/* Copy Own */}
          <Form.Check
            type="radio"
            id="link-copy-own"
            name="linkMode"
            label={
              <div>
                <strong>Copy &amp; Own</strong>
                <div style={{ fontSize: 12, color: '#a8a5a0' }}>
                  Create a full copy owned by this story. The copy won't appear as a standalone scene.
                </div>
              </div>
            }
            checked={mode === 'copy-own'}
            onChange={() => setMode('copy-own')}
            className="mb-3"
          />

          {/* Move Own */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
            <Form.Check
              type="radio"
              id="link-move-own"
              name="linkMode"
              label={
                <div>
                  <strong style={{ opacity: moveDisabled ? 0.4 : 1 }}>Move &amp; Own</strong>
                  <div style={{ fontSize: 12, color: '#a8a5a0', opacity: moveDisabled ? 0.4 : 1 }}>
                    Move the original scene into this story as owned. It won't appear standalone.
                  </div>
                </div>
              }
              checked={mode === 'move-own'}
              onChange={() => !moveDisabled && setMode('move-own')}
              disabled={moveDisabled}
              className="mb-1"
            />
            {moveDisabled && !checkingLinks && moveReason && (
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip id="move-disabled-tip">{moveReason}</Tooltip>}
              >
                <span style={{ cursor: 'help', marginTop: 4 }}>
                  <InfoCircle size={14} style={{ color: '#a8a5a0' }} />
                </span>
              </OverlayTrigger>
            )}
            {checkingLinks && (
              <Spinner animation="border" size="sm" style={{ marginTop: 4 }} />
            )}
          </div>
        </Form>
      </div>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => setStep('pick')}>
          Back
        </Button>
        <Button
          variant="primary"
          disabled={!selectedSceneId}
          onClick={() => {
            if (selectedSceneId) onSelect(selectedSceneId, mode);
          }}
        >
          Add Scene
        </Button>
      </Modal.Footer>
    </>
  );
};

export default ScenePicker;
